import { sourceIcon, type BrandIcon } from "./icons/source-icons";

/**
 * What a link says about the thing it points at before anything is fetched:
 * its kind in words, its name when the URL carries one, where it lives, and
 * the product's mark. Pills and cards lead with this, so a link reads as the
 * document, message or issue it names rather than as an app or an address; a
 * live preview only ever refines it.
 */
export type LinkEntity = {
  /** The thing, in words: "Word document", "Slack message", "Pull request". */
  kind: string;
  /** Its name when the URL carries one: a file, folder, page, site, channel or issue. */
  title: string | null;
  /** Where it lives when the URL says: a SharePoint site, a Slack workspace, a repository. */
  context: string | null;
  icon: BrandIcon | null;
  /** The service it lives in, as people name it. */
  service: string;
  /** The host answers an anonymous fetch only with a sign-in page, so there is nothing public to read. */
  signInOnly: boolean;
};

type Parsed = Omit<LinkEntity, "icon" | "signInOnly"> & { icon: string; signInOnly?: boolean };

/** The entity a link names, or null for a link we know nothing particular about. */
export function linkEntity(href: string, liveKind?: string | null): LinkEntity | null {
  const url = secureUrl(href);
  if (!url) return null;
  const parsed = parse(url);
  if (!parsed) return null;
  const refined = liveKind ? refineKind(parsed, liveKind) : parsed;
  return { ...refined, icon: sourceIcon(refined.icon), signInOnly: refined.signInOnly ?? false };
}

/** A pill's words before a live preview arrives: the name when the link carries one, else the kind. */
export function linkEntityLabel(entity: LinkEntity): string {
  return entity.title ?? entity.kind;
}

const OUTLOOK_HOSTS = new Set(["outlook.office.com", "outlook.office365.com", "outlook.live.com"]);

/** Each service a URL can name, and the reader for its links. */
const PARSERS: Array<[(host: string) => boolean, (url: URL) => Parsed | null]> = [
  [(host) => host.endsWith(".sharepoint.com"), sharePoint],
  [(host) => host === "onedrive.live.com" || host === "1drv.ms", oneDrive],
  [(host) => host === "teams.microsoft.com" || host === "teams.cloud.microsoft", teams],
  [(host) => host === "m365.cloud.microsoft" || host === "copilot.microsoft.com", copilot],
  [(host) => host === "docs.google.com" || host === "drive.google.com", google],
  [(host) => host === "mail.google.com", gmail],
  [(host) => host === "calendar.google.com", googleCalendar],
  [(host) => OUTLOOK_HOSTS.has(host), outlook],
  [(host) => host.endsWith(".slack.com") && host !== "api.slack.com", slack],
  [(host) => host === "github.com", gitHub],
  [(host) => host === "linear.app", linear],
  [(host) => host === "notion.so" || host === "www.notion.so" || host.endsWith(".notion.site"), notion],
  [(host) => /^app(?:-[a-z0-9]+)?\.hubspot\.com$/.test(host), hubSpot],
  [(host) => host === "go.xero.com", xero],
  [(host) => host === "dashboard.stripe.com", stripe],
  [(host) => host.endsWith(".lightning.force.com") || host.endsWith(".my.salesforce.com"), salesforce],
];

function parse(url: URL): Parsed | null {
  const host = url.hostname.toLowerCase();
  const reader = PARSERS.find(([matches]) => matches(host))?.[1];
  return reader ? reader(url) : null;
}

function copilot(): Parsed {
  return { kind: "Copilot chat", title: null, context: null, icon: "copilot", service: "Microsoft 365 Copilot", signInOnly: true };
}

function googleCalendar(url: URL): Parsed {
  const event = /\/event\b|\/eventedit\//.test(url.pathname) || url.searchParams.has("eid");
  return { kind: event ? "Calendar event" : "Calendar", title: null, context: null, icon: "google_calendar", service: "Google Calendar", signInOnly: true };
}

function outlook(url: URL): Parsed {
  const calendar = url.pathname.includes("/calendar");
  return { kind: calendar ? "Calendar event" : "Email", title: null, context: null, icon: "outlook_mail", service: "Outlook", signInOnly: true };
}

// Office: the one-letter type in a share link, or the file's extension.
const OFFICE_KINDS: Record<string, { kind: string; icon?: string }> = {
  w: { kind: "Word document", icon: "word" },
  x: { kind: "Excel workbook", icon: "excel" },
  p: { kind: "PowerPoint presentation", icon: "powerpoint" },
  f: { kind: "Folder" },
  o: { kind: "OneNote notebook" },
  b: { kind: "PDF" },
  v: { kind: "Video" },
  i: { kind: "Image" },
  u: { kind: "File" },
};
const EXTENSION_TYPES: Array<[RegExp, string]> = [
  [/\.(?:docx?|docm|dotx|rtf)$/i, "w"],
  [/\.(?:xlsx?|xlsm|xlsb|csv)$/i, "x"],
  [/\.(?:pptx?|pptm|ppsx)$/i, "p"],
  [/\.one$/i, "o"],
  [/\.pdf$/i, "b"],
  [/\.(?:mp4|mov|m4v|webm)$/i, "v"],
  [/\.(?:png|jpe?g|gif|webp|heic)$/i, "i"],
];
const LIVE_KINDS: Record<string, string> = {
  "Word document": "word",
  "Excel workbook": "excel",
  "PowerPoint presentation": "powerpoint",
  "Google Doc": "google_docs",
  "Google Sheet": "google_sheets",
  "Google Slides": "google_slides",
};

function typeOfName(name: string | null): string | null {
  if (!name) return null;
  return EXTENSION_TYPES.find(([pattern]) => pattern.test(name))?.[1] ?? null;
}

function officeKind(type: string | null, fallbackIcon: string) {
  const entry = (type && OFFICE_KINDS[type]) || OFFICE_KINDS.u!;
  return { kind: entry.kind, icon: entry.icon ?? fallbackIcon };
}

type SharePointHome = { service: string; icon: string; personal: boolean };

function sharePoint(url: URL): Parsed {
  const personal = url.hostname.toLowerCase().endsWith("-my.sharepoint.com");
  const home: SharePointHome = personal
    ? { service: "OneDrive", icon: "onedrive", personal }
    : { service: "SharePoint", icon: "sharepoint", personal };
  const { parts, shareType, token } = sharePointPath(url);
  const site = siteOf(parts);
  const context = personal ? null : site;
  if (token) {
    return { ...officeKind(shareType, home.icon), title: null, context: personal ? null : parts[0] ?? null, service: home.service };
  }
  const fileName = url.searchParams.get("file");
  if (fileName) return { ...officeKind(typeOfName(fileName) ?? shareType, home.icon), title: fileName, context, service: home.service };
  return sharePointPlace(parts, site, context, shareType, home) ?? sharePointItem(parts, context, shareType, home);
}

/** The path a link names: a share link's own path, or the folder a library view shows. */
function sharePointPath(url: URL) {
  let parts = segments(url.pathname);
  let shareType: string | null = null;
  let token = false;
  const share = /^:([a-z]):$/i.exec(parts[0] ?? "");
  if (share) {
    shareType = share[1]!.toLowerCase();
    token = parts[1] !== "r";
    parts = parts.slice(2);
  }
  // Library views name the folder they show in `id`; Doc.aspx names its file in `file`.
  const shownPath = url.searchParams.get("id");
  if (shownPath?.startsWith("/")) parts = segments(shownPath);
  return { parts, shareType, token };
}

/** A site, page, list, library or OneDrive itself, rather than one item in it. */
function sharePointPlace(parts: string[], site: string | null, context: string | null, shareType: string | null, home: SharePointHome): Parsed | null {
  const base = { icon: home.icon, service: home.service };
  const last = parts.at(-1) ?? null;
  const lists = parts.indexOf("Lists");
  if (lists >= 0 && parts[lists + 1]) return { ...base, kind: "SharePoint list", title: parts[lists + 1]!, context };
  if (last && /\.aspx$/i.test(last)) {
    const page = last.replace(/\.aspx$/i, "");
    if (parts.at(-2) === "SitePages") {
      return /^home$/i.test(page) && site
        ? { ...base, kind: "SharePoint site", title: site, context: null }
        : { ...base, kind: "SharePoint page", title: humanise(page), context };
    }
    const forms = parts.indexOf("Forms");
    if (forms > 0) return { ...base, kind: "Document library", title: parts[forms - 1]!, context };
    return { ...officeKind(shareType, home.icon), title: null, context, service: home.service };
  }
  const inSite = parts[0] === "sites" || parts[0] === "teams";
  if (inSite && parts.length <= 2 && site) return { ...base, kind: "SharePoint site", title: site, context: null };
  if (home.personal && parts.length <= 3) return { ...base, kind: "OneDrive", title: null, context: null };
  if (inSite && parts.length === 3 && !shareType) return { ...base, kind: "Document library", title: last, context };
  return null;
}

/** One file or folder, named by its last path segment. */
function sharePointItem(parts: string[], context: string | null, shareType: string | null, home: SharePointHome): Parsed {
  const last = parts.at(-1) ?? null;
  // A generic share type says less than the file's own extension.
  const type = (shareType !== "u" ? shareType : null) ?? typeOfName(last) ?? (shareType ?? (last && !last.includes(".") ? "f" : null));
  return { ...officeKind(type, home.icon), title: last, context, service: home.service };
}

function siteOf(parts: string[]): string | null {
  const index = parts.findIndex((part) => part === "sites" || part === "teams");
  return index >= 0 ? parts[index + 1] ?? null : null;
}

function oneDrive(url: URL): Parsed {
  const type = url.hostname === "1drv.ms" ? segments(url.pathname)[0]?.toLowerCase() ?? null : null;
  return { ...officeKind(type && OFFICE_KINDS[type] ? type : null, "onedrive"), title: null, context: null, service: "OneDrive" };
}

function teams(url: URL): Parsed {
  const parts = segments(url.pathname);
  const params = url.searchParams;
  const team = params.get("teamName");
  const base = { icon: "teams", service: "Microsoft Teams", signInOnly: true };
  if (parts[0] !== "l") return { ...base, kind: "Microsoft Teams", title: null, context: null };
  switch (parts[1]) {
    case "message": {
      const chat = /@(?:unq\.gbl\.spaces|thread\.v2)$/.test(parts[2] ?? "");
      const reply = Boolean(params.get("parentMessageId")) && params.get("parentMessageId") !== parts[3];
      const channel = params.get("channelName");
      return {
        ...base,
        kind: chat ? "Teams chat message" : reply ? "Teams reply" : "Teams message",
        title: channel ? `Message in ${channel}` : null,
        context: team,
      };
    }
    case "channel":
      return { ...base, kind: "Teams channel", title: parts[3] ?? null, context: team };
    case "chat":
      return { ...base, kind: "Teams chat", title: null, context: null };
    case "meetup-join":
      return { ...base, kind: "Teams meeting", title: null, context: null };
    case "team":
      return { ...base, kind: "Team", title: parts[3] ?? null, context: null };
    case "file": {
      const object = params.get("objectUrl");
      const name = object ? segments(safeUrl(object)?.pathname ?? "").at(-1) ?? null : null;
      const type = typeOfName(name) ?? typeOfName(`.${params.get("fileType") ?? ""}`);
      return { ...base, ...officeKind(type, "teams"), title: name, context: team };
    }
    default:
      return { ...base, kind: "Microsoft Teams", title: null, context: null };
  }
}

function google(url: URL): Parsed {
  const parts = segments(url.pathname).filter((part, index, all) => !(part === "u" || (all[index - 1] === "u" && /^\d+$/.test(part))));
  const drive = { title: null, context: null, service: "Google Drive" };
  if (url.hostname === "docs.google.com") {
    switch (parts[0]) {
      case "document": return { ...drive, kind: "Google Doc", icon: "google_docs", service: "Google Docs" };
      case "spreadsheets": return { ...drive, kind: "Google Sheet", icon: "google_sheets", service: "Google Sheets" };
      case "presentation": return { ...drive, kind: "Google Slides", icon: "google_slides", service: "Google Slides" };
      case "forms": return { ...drive, kind: "Google Form", icon: "google_drive", service: "Google Forms" };
      case "drawings": return { ...drive, kind: "Google Drawing", icon: "google_drive" };
      default: return { ...drive, kind: "Google Drive file", icon: "google_drive" };
    }
  }
  if (parts.includes("folders")) return { ...drive, kind: "Drive folder", icon: "google_drive" };
  return { ...drive, kind: "Google Drive file", icon: "google_drive" };
}

function gmail(url: URL): Parsed {
  const [folder, second] = url.hash.replace(/^#/, "").split("/");
  const context = folder === "inbox" ? "Inbox" : folder === "sent" ? "Sent" : folder === "label" && second ? decode(second.replace(/\+/g, " ")) : null;
  return { kind: "Email", title: null, context, icon: "gmail", service: "Gmail", signInOnly: true };
}

function slack(url: URL): Parsed {
  const parts = segments(url.pathname);
  const workspace = url.hostname.split(".")[0] ?? null;
  const base = { context: workspace === "app" ? null : workspace, icon: "slack", service: "Slack", signInOnly: true };
  if (parts[0] === "archives" && parts[2]?.startsWith("p")) {
    return { ...base, kind: url.searchParams.has("thread_ts") ? "Slack reply" : "Slack message", title: null };
  }
  if (parts[0] === "archives" && parts[1]) return { ...base, kind: "Slack channel", title: null };
  if (parts[0] === "files" && parts[3]) return { ...base, kind: "Slack file", title: parts[3] };
  return { ...base, kind: "Slack", title: null };
}

const GITHUB_PAGES = new Set([
  "about", "apps", "codespaces", "enterprise", "explore", "features", "login", "marketplace",
  "notifications", "orgs", "pricing", "pulls", "issues", "search", "settings", "sponsors", "topics",
]);

function gitHub(url: URL): Parsed | null {
  const [owner, repo, section, id, ...rest] = segments(url.pathname);
  if (!owner || GITHUB_PAGES.has(owner.toLowerCase())) return null;
  const base = { icon: "github", service: "GitHub" };
  if (!repo) return { ...base, kind: "GitHub account", title: owner, context: null };
  const name = `${owner}/${repo}`;
  switch (section) {
    case "pull": return { ...base, kind: "Pull request", title: id ? `${name} #${id}` : name, context: null };
    case "issues": return { ...base, kind: id ? "Issue" : "Issues", title: id ? `${name} #${id}` : name, context: null };
    case "discussions": return { ...base, kind: "Discussion", title: id ? `${name} #${id}` : name, context: null };
    case "commit": return { ...base, kind: "Commit", title: id ? `${name}@${id.slice(0, 7)}` : name, context: null };
    case "blob": return { ...base, kind: "File", title: rest.at(-1) ?? name, context: name };
    case "tree": return { ...base, kind: "Folder", title: rest.at(-1) ?? name, context: name };
    case "releases": return { ...base, kind: "Release", title: rest.at(-1) ?? name, context: rest.length ? name : null };
    case "actions": return { ...base, kind: "Workflow run", title: name, context: null };
    default: return { ...base, kind: "Repository", title: name, context: null };
  }
}

function linear(url: URL): Parsed | null {
  const [workspace, section, key, slug] = segments(url.pathname);
  const base = { icon: "linear", service: "Linear", context: workspace ?? null, signInOnly: true };
  if (section === "issue" && key) return { ...base, kind: "Linear issue", title: slug ? `${key} ${humanise(slug)}` : key };
  if (section === "project" && key) return { ...base, kind: "Linear project", title: humanise(key.replace(/-[0-9a-f]{8,}$/i, "")) };
  return workspace ? { ...base, kind: "Linear", title: null } : null;
}

function notion(url: URL): Parsed {
  const last = segments(url.pathname).at(-1) ?? "";
  const words = last.replace(/-?[0-9a-f]{32}$/i, "");
  return { kind: "Notion page", title: words ? humanise(words) : null, context: null, icon: "notion", service: "Notion" };
}

const HUBSPOT_OBJECTS: Record<string, string> = { "0-1": "contact", "0-2": "company", "0-3": "deal", "0-5": "ticket" };

function hubSpot(url: URL): Parsed {
  const parts = segments(url.pathname);
  const record = parts.indexOf("record");
  const object = (record >= 0 && HUBSPOT_OBJECTS[parts[record + 1] ?? ""]) || (["contact", "company", "deal", "ticket"].find((name) => parts.includes(name)) ?? null);
  return { kind: object ? `HubSpot ${object}` : "HubSpot", title: null, context: null, icon: "hubspot", service: "HubSpot", signInOnly: true };
}

function xero(url: URL): Parsed {
  const path = url.pathname.toLowerCase();
  const kind = path.startsWith("/accountsreceivable") ? "Xero invoice"
    : path.startsWith("/accountspayable") ? "Xero bill"
      : path.startsWith("/contacts") ? "Xero contact"
        : path.startsWith("/bank") ? "Xero bank account" : "Xero";
  return { kind, title: null, context: null, icon: "xero", service: "Xero", signInOnly: true };
}

const STRIPE_OBJECTS: Record<string, string> = {
  payments: "payment", customers: "customer", invoices: "invoice", subscriptions: "subscription",
  products: "product", payouts: "payout", refunds: "refund", disputes: "dispute",
};

function stripe(url: URL): Parsed {
  const parts = segments(url.pathname).filter((part) => part !== "test");
  const object = STRIPE_OBJECTS[parts[0] ?? ""];
  return {
    kind: object ? `Stripe ${object}` : "Stripe",
    title: object && parts[1] ? parts[1] : null,
    context: null, icon: "stripe", service: "Stripe", signInOnly: true,
  };
}

function salesforce(url: URL): Parsed {
  const parts = segments(url.pathname);
  const object = parts[0] === "lightning" && parts[1] === "r" ? parts[2] : null;
  return {
    kind: object ? `Salesforce ${humanise(object.replace(/__c$/i, "")).toLowerCase()}` : "Salesforce",
    title: null, context: null, icon: "salesforce", service: "Salesforce", signInOnly: true,
  };
}

/** A live preview knows an opaque link's real type; its mark follows. */
function refineKind(parsed: Parsed, liveKind: string): Parsed {
  const icon = LIVE_KINDS[liveKind];
  return { ...parsed, kind: liveKind, ...(icon ? { icon } : {}) };
}

function secureUrl(href: string): URL | null {
  const url = safeUrl(href);
  if (!url || url.protocol !== "https:" || url.username || url.password || url.port) return null;
  return url;
}

function safeUrl(href: string): URL | null {
  try { return new URL(href); } catch { return null; }
}

function segments(path: string): string[] {
  return path.split("/").filter(Boolean).map(decode);
}

function decode(value: string): string {
  try { return decodeURIComponent(value); } catch { return value; }
}

function humanise(slug: string): string {
  const words = slug.replace(/[-_+]+/g, " ").replace(/\s+/g, " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : slug;
}
